import {
  openNewTabWithCheck,
  redirectWithCheck,
} from '@web-platform/shared-banana-utils-frontend/src/helpers/redirects';
import { shouldHardRedirect } from '@web-platform/shared-banana-utils-frontend/src/helpers/shouldHardRedirect';
import {
  buildUrl,
  isMultiDestination,
} from '@web-platform/shared-banana-utils-frontend/src/helpers/url-builder';
import {
  getInboundDate,
  getIsAnytime,
  getOutboundDate,
} from '@web-platform/shared-banana-utils-frontend/src/helpers/userSearchUtils';
import { SEARCH_PLACE_TYPES } from '@web-platform/shared-universal-constants/src/places';
import { SCREEN_NAMES } from '@web-platform/shared-universal-constants/src/screenName';
import { searchParamsChanged } from '@web-platform/shared-universal-redux/src/actions/user';
import { RESULTS_VIEW_QUERY_PARAM } from '@web-platform/shared-universal-redux/src/selectors/resultsView';
import { getCurrentScreenName } from '@web-platform/shared-universal-redux/src/selectors/trustedFunnel';
import { getUserSearch } from '@web-platform/shared-universal-redux/src/state';
import { translate } from '@web-platform/shared-universal-translations';
import { buildHotelsSearchURL } from '@web-platform/shared-universal-utils-frontend/src/helpers/buildHotelsSearchURL';
import { buildPackagesRedirectUrl } from '@web-platform/shared-universal-utils-frontend/src/helpers/buildPackagesRedirectUrl';
import { gcFareAttributesToRedux } from '@web-platform/shared-universal-utils-frontend/src/helpers/fareAttributes/mappers';
import {
  getDestinationFromFirstLeg,
  getOriginFromFirstLeg,
} from '@web-platform/shared-universal-utils-frontend/src/helpers/getSearchPlaceFromFirstLeg';
import { isPackagesSearchEligible } from '@web-platform/shared-universal-utils-frontend/src/helpers/isPackagesSearchEligible';
import {
  getCulture,
  formatDateNonlocalised,
  DATE_TYPE,
  getDateType,
} from '@web-platform/shared-universal-utils-frontend-localisation';
import { addDays } from 'date-fns/addDays';
import { differenceInCalendarDays } from 'date-fns/differenceInCalendarDays';
import { parseISO } from 'date-fns/parseISO';
import isEqual from 'lodash/isEqual';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';

import { searchControlDisplayChanged } from '../../actions/searchControls';
import { getLayoversDuration, getLayoversExcludedAirports } from '../../state';

import type { ContextSearchPlace as FSCContextSearchPlace } from '@skyscanner-internal/global-components/flights-search-controls/common/types/context';
import type { InternalGCSearchParams } from '@skyscanner-internal/global-components/flights-search-controls/common/types/search';
import type { FlightsSearchSummaryDateContext } from '@web-platform/shared-universal-types/src/components/useNavigate';
import type {
  ContextCulture,
  ContextSearchPlace,
  FareAttribute,
} from '@web-platform/shared-universal-types/src/context';
import type { DateString } from '@web-platform/shared-universal-types/src/dateString';
import type {
  PartialSearchParams,
  SearchParams,
  SearchState,
} from '@web-platform/shared-universal-types/src/redux/state/appInitial/user/search';
import type { IMBDEventTracking } from '@web-platform/shared-universal-types-tracking/src/IMBDEventTracking';
import type {
  EntityId,
  Maybe,
  Merge,
  Nullable,
} from '@web-platform/shared-universal-types-utils/src/utils';

export const PARALLEL_SEARCH_DELAY = 500;

const getHotelSearchDates = (outboundDate: DateString, inboundDate: Maybe<DateString>) => {
  const defaultCheckoutDate = formatDateNonlocalised(addDays(parseISO(outboundDate), 1));

  if (!inboundDate) {
    return [outboundDate, defaultCheckoutDate];
  }

  const daysDiff = differenceInCalendarDays(inboundDate, outboundDate);

  if (daysDiff < 1 || daysDiff > 30) {
    return [outboundDate, defaultCheckoutDate];
  }

  return [outboundDate, inboundDate];
};

const getParallelSearchUrl = (searchParams: SearchParams): Nullable<string> => {
  if (!searchParams.shouldPerformParallelSearch) {
    return null;
  }

  const origin = getOriginFromFirstLeg(searchParams);
  const destination = getDestinationFromFirstLeg(searchParams);
  const outboundDate = getOutboundDate(searchParams);
  const inboundDate = getInboundDate(searchParams);
  const outboundDateType = getDateType(outboundDate, getIsAnytime(searchParams));
  const isMultiCity = isMultiDestination(searchParams);

  const isFocusedSearchDestination =
    destination?.type === SEARCH_PLACE_TYPES.CITY ||
    destination?.type === SEARCH_PLACE_TYPES.AIRPORT;
  const isFocusedSearchOrigin =
    origin?.type === SEARCH_PLACE_TYPES.CITY || origin?.type === SEARCH_PLACE_TYPES.AIRPORT;
  const isFocusedOutboundDate = outboundDateType === DATE_TYPE.SPECIFIC_DATE;

  if (
    !isFocusedSearchDestination ||
    !isFocusedSearchOrigin ||
    !isFocusedOutboundDate ||
    !outboundDate ||
    !destination ||
    (!destination.geoContainerId && !destination.geoId) ||
    isMultiCity
  ) {
    return null;
  }

  const [checkinDate, checkoutDate] = getHotelSearchDates(outboundDate, inboundDate);

  return buildHotelsSearchURL({
    entityId: destination.geoContainerId || destination.geoId || '',
    checkinDate,
    checkoutDate,
    source: 'parallel_search',
  });
};

export const getPackagesParallelSearchUrl = (
  searchParams: SearchParams,
  culture: ContextCulture,
): Nullable<string> => {
  if (!searchParams.shouldPerformPackagesParallelSearch) {
    return null;
  }

  const origin = getOriginFromFirstLeg(searchParams);
  const destination = getDestinationFromFirstLeg(searchParams);
  const outboundDate = getOutboundDate(searchParams);
  const inboundDate = getInboundDate(searchParams);

  const isFocusedSearchOrigin =
    origin?.type === SEARCH_PLACE_TYPES.CITY || origin?.type === SEARCH_PLACE_TYPES.AIRPORT;

  if (
    !origin ||
    !destination ||
    !outboundDate ||
    !inboundDate ||
    !isFocusedSearchOrigin ||
    getDateType(outboundDate, getIsAnytime(searchParams)) !== DATE_TYPE.SPECIFIC_DATE ||
    getDateType(inboundDate) !== DATE_TYPE.SPECIFIC_DATE
  ) {
    return null;
  }

  if (
    !isPackagesSearchEligible({
      culture,
      destination,
      inboundDate,
      origin,
      outboundDate,
      tripType: searchParams.tripType,
    })
  ) {
    return null;
  }

  return buildPackagesRedirectUrl(
    {
      adults: searchParams.adults,
      child_ages: searchParams.child_ages,
      destinationId: destination.id,
      inboundDate,
      originId: origin.id,
      outboundDate,
    },
    'parallel_search',
  );
};

const getUpdatedUserSearch = (
  userSearch: SearchState,
  incomingParams: PartialSearchParams,
): SearchParams => {
  const updatedUserSearch = { ...userSearch, ...incomingParams };

  // merge legs
  if (
    incomingParams &&
    incomingParams.legs &&
    incomingParams.legs[0] &&
    userSearch.legs &&
    userSearch.legs[0]
  ) {
    updatedUserSearch.legs = [{ ...userSearch.legs[0], ...incomingParams.legs[0] }];
  }

  // @ts-expect-error ts-migrate(7006): Suppressed to be able to turn on strict mode. Do not copy.
  return updatedUserSearch;
};

type translatedStrings = {
  ktxtURLFlights: string;
  ktxtURLFlightsFrom: string;
  ktxtURLTransport: string;
};

export const useCombinedResultsSearch = (
  parallelSearchTracking?: IMBDEventTracking,
  packagesParallelSearchTracking?: IMBDEventTracking,
) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const userSearch = useSelector(getUserSearch);
  const layoverDuration = useSelector(getLayoversDuration);
  const layoverExcludedAirports = useSelector(getLayoversExcludedAirports);
  const { pathname, search } = useLocation();
  const screenName = useSelector(getCurrentScreenName);
  const culture = getCulture();

  const strings: translatedStrings = {
    ktxtURLFlights: translate('ktxtURLFlights'),
    ktxtURLFlightsFrom: translate('ktxtURLFlightsFrom'),
    ktxtURLTransport: translate('ktxtURLTransport'),
  };

  const replacePathnameOnHomepage = () => {
    if (screenName === SCREEN_NAMES.HOME_PAGE) {
      navigate({ pathname, search }, { replace: true });
    }
  };

  // TODO: QUAR-1810, STAR-215: sponsoredAgent should only propagate when navigating to a destination from a
  // place card (handled by usePlacesRedirectHandlers). On a new search it must be cleared from
  // both the URL and userSearch. Consolidate the two Redux slices as part of a broader cleanup.
  const dispatchAndRedirect = (updatedUserSearch: Nullable<SearchParams>): void => {
    if (!updatedUserSearch) {
      return undefined;
    }

    const sanitisedUserSearch = { ...updatedUserSearch, sponsoredAgent: undefined };

    // TODO: STAR-389: Cleanup these lines reading resultsView from the URL after POC is done
    const currentResultsView = new URLSearchParams(search).get(RESULTS_VIEW_QUERY_PARAM);
    const extraSearchParams = new URLSearchParams();
    if (currentResultsView) {
      extraSearchParams.set(RESULTS_VIEW_QUERY_PARAM, currentResultsView);
    }

    const flightsUrl = buildUrl(strings, sanitisedUserSearch, extraSearchParams, {
      duration: layoverDuration,
      excludedAirports: layoverExcludedAirports,
    });
    const origin = getOriginFromFirstLeg(sanitisedUserSearch);
    const destination = getDestinationFromFirstLeg(sanitisedUserSearch);
    const outboundDate = getOutboundDate(sanitisedUserSearch);
    const isAnytime = getIsAnytime(sanitisedUserSearch);
    const isMultiCity = isMultiDestination(sanitisedUserSearch);
    const isHardRedirect = isMultiCity
      ? true
      : shouldHardRedirect(destination, origin, outboundDate, isAnytime);

    if (!isHardRedirect && !isEqual(userSearch, sanitisedUserSearch)) {
      dispatch(searchParamsChanged(sanitisedUserSearch));
    }

    if (!isHardRedirect) {
      dispatch(searchControlDisplayChanged(false)); // Hide Search Controls if soft navigating
    }

    // The condition to check screenName here will be redundant because parallelSearchUrl will be null if not on home page when we update useParallelSearch.ts
    // See https://skyscanner.slack.com/archives/C05B79J7MM4/p1759922199972259?thread_ts=1759920518.807059&cid=C05B79J7MM4
    // Navigation matrix: Packages takes priority over Stays when both are selected and eligible
    const isOnHomePage = screenName === SCREEN_NAMES.HOME_PAGE;
    let packagesUrl: Nullable<string> = null;
    let staysUrl: Nullable<string> = null;

    if (isOnHomePage) {
      // Try Packages first (higher priority)
      packagesUrl = getPackagesParallelSearchUrl(sanitisedUserSearch, culture);
      // If Packages not eligible, fall back to Stays
      if (!packagesUrl) {
        staysUrl = getParallelSearchUrl(sanitisedUserSearch);
      }
    }

    // Determine navigation based on matrix:
    // - Packages URL → Packages in current tab, Flights in new tab
    // - Stays URL → Stays in current tab, Flights in new tab
    // - Neither → Flights in current tab
    if (packagesUrl) {
      setTimeout(() => {
        packagesParallelSearchTracking?.trackInteraction(undefined, {
          shouldPerformParallelSearch: !!sanitisedUserSearch.shouldPerformParallelSearch,
          shouldPerformPackagesParallelSearch:
            !!sanitisedUserSearch.shouldPerformPackagesParallelSearch,
        });
        redirectWithCheck(navigate, packagesUrl, sanitisedUserSearch, true);
      }, PARALLEL_SEARCH_DELAY);
      return openNewTabWithCheck(navigate, flightsUrl, sanitisedUserSearch, true);
    }

    if (staysUrl) {
      setTimeout(() => {
        parallelSearchTracking?.trackInteraction(undefined, {
          shouldPerformParallelSearch: !!sanitisedUserSearch.shouldPerformParallelSearch,
          shouldPerformPackagesParallelSearch:
            !!sanitisedUserSearch.shouldPerformPackagesParallelSearch,
        });
        redirectWithCheck(navigate, staysUrl, sanitisedUserSearch, true);
      }, PARALLEL_SEARCH_DELAY);
      return openNewTabWithCheck(navigate, flightsUrl, sanitisedUserSearch, true);
    }

    // No parallel search: flights in current tab only
    return redirectWithCheck(navigate, flightsUrl, sanitisedUserSearch, isHardRedirect);
  };

  return {
    goToCombinedResults: (incomingParams: PartialSearchParams = {}) => {
      const updatedUserSearch = getUpdatedUserSearch(userSearch, incomingParams);
      replacePathnameOnHomepage();
      dispatchAndRedirect(updatedUserSearch);
    },
    goToCombinedResultsFromFlightsSearchControlsGC: (incomingParams: InternalGCSearchParams) => {
      // reusing banana's user search update function
      // FIXME: WP-139 FSC types should use shared EntityId directly after moving the GC into web-platform repo
      const typedParams = incomingParams as InternalGCSearchParamsWithEntityIdBrandedType;
      // GC emits fareAttributes in kebab-case via URL parameter; gcFareAttributesToRedux converts to camelCase at this boundary.
      const { fareAttributes: gcFareAttributes, ...gcParamsWithoutFareAttributes } = typedParams;
      const updated = getUpdatedUserSearch(userSearch, {
        ...gcParamsWithoutFareAttributes,
        fareAttributes: gcFareAttributesToRedux(gcFareAttributes ?? []),
      });

      replacePathnameOnHomepage();
      dispatchAndRedirect(updated);
    },
    goToCombinedResultsFromFlightsSearchSummaryGC: (
      incomingParams: FlightsSearchSummaryDateContext,
    ) => {
      // reusing banana's user search update function
      const updated = getUpdatedUserSearch(userSearch, {
        legs: [
          {
            date: incomingParams.outboundDate as DateString,
            ...(incomingParams.inboundDate
              ? { return_date: incomingParams.inboundDate as DateString }
              : {}),
          },
        ],
      });

      dispatchAndRedirect(updated);
    },
  };
};

type InternalGCContextPlace = ContextSearchPlace &
  Merge<
    FSCContextSearchPlace,
    {
      geoId?: EntityId;
      geoContainerId?: EntityId;
    }
  >;
type InternalGCSearchParamsWithEntityIdBrandedType = Merge<
  InternalGCSearchParams,
  {
    fareAttributes?: FareAttribute[];
    legs: Array<
      Merge<
        InternalGCSearchParams['legs'][number],
        {
          origin: InternalGCContextPlace;
          destination?: InternalGCContextPlace;
          placeOfStay?: EntityId;
        }
      >
    >;
  }
>;
